/**
 * Sustituye un frame preview del stack por DICOM real (solo ese SOP).
 * No cambia el renderMode global del estudio.
 * Soporta DIRECT_URL (dicomUrl https) y RANGE_ZIP (archiveUrl + offsets).
 */
import { RENDER_MODE } from './renderMode'
import { loadInstanceForMode } from './loadInstanceForMode'

/**
 * ¿Se puede descargar DICOM de este frame (URL directa o range zip)?
 * @param {object} fileData
 * @returns {boolean}
 */
export function frameHasDicomSource(fileData) {
  if (!fileData) return false
  const dicomUrl =
    fileData.dicomUrl != null ? String(fileData.dicomUrl).trim() : ''
  if (/^https?:\/\//i.test(dicomUrl)) return true

  const offset =
    fileData.byteOffset != null ? Number(fileData.byteOffset) : NaN
  const length =
    fileData.byteLength != null ? Number(fileData.byteLength) : NaN
  const archive =
    fileData.archiveUrl != null ? String(fileData.archiveUrl).trim() : ''
  if (
    archive &&
    Number.isFinite(offset) &&
    offset >= 0 &&
    Number.isFinite(length) &&
    length > 0
  ) {
    return true
  }
  return false
}

/**
 * ¿El frame aún no es un DICOM real listo para medición / geometría?
 * @param {object} fileData stack item (files[i])
 * @returns {boolean}
 */
export function frameNeedsDicomUpgrade(fileData) {
  if (!fileData) return false

  const hasDataset =
    fileData.image &&
    fileData.image.data &&
    typeof fileData.image.data.string === 'function'
  const isWebRaster =
    fileData.image && typeof fileData.image.getImage === 'function'

  // DICOM real en heap (no web-image-loader)
  if (
    hasDataset &&
    !isWebRaster &&
    (fileData.renderKind === 'dicom' || fileData.renderKind == null)
  ) {
    const id = fileData.imageId != null ? String(fileData.imageId) : ''
    if (
      id.startsWith('wadouri:') ||
      id.startsWith('wadors:') ||
      id.startsWith('dicomfile:') ||
      id.startsWith('dicomweb:') ||
      fileData.renderKind === 'dicom'
    ) {
      return false
    }
  }

  // Preview / sin dataset / web residual → upgrade si hay fuente (o intentar)
  if (fileData.renderKind === 'preview') return true
  if (isWebRaster) return true
  if (!hasDataset) return true
  if (fileData.dicomUrl && fileData.renderKind !== 'dicom') return true
  if (
    fileData.renderKind === 'dicom' &&
    (!fileData.image || !hasDataset)
  ) {
    return true
  }
  return false
}

/**
 * Construye `inst` para loadInstanceForMode a partir del item del stack.
 * @param {object} fileData
 * @returns {object}
 */
export function buildInstFromFileData(fileData) {
  const dicomUrl =
    fileData.dicomUrl != null ? String(fileData.dicomUrl).trim() : ''
  const archiveUrl =
    fileData.archiveUrl != null ? String(fileData.archiveUrl).trim() : ''
  const byteOffset =
    fileData.byteOffset != null && Number.isFinite(Number(fileData.byteOffset))
      ? Number(fileData.byteOffset)
      : null
  const byteLength =
    fileData.byteLength != null && Number.isFinite(Number(fileData.byteLength))
      ? Number(fileData.byteLength)
      : null

  let dicomFetchType =
    fileData.dicomFetchType != null ? String(fileData.dicomFetchType) : null
  if (
    !dicomFetchType &&
    archiveUrl &&
    byteOffset != null &&
    byteLength != null &&
    byteLength > 0
  ) {
    dicomFetchType = 'RANGE_ZIP'
  } else if (!dicomFetchType && /^https?:\/\//i.test(dicomUrl)) {
    dicomFetchType = 'DIRECT_URL'
  }

  return {
    sopInstanceUID: fileData.sopInstanceUID || null,
    dicomUrl: dicomUrl || null,
    url: /^https?:\/\//i.test(dicomUrl) ? dicomUrl : null,
    archiveUrl: archiveUrl || null,
    byteOffset,
    byteLength,
    dicomFetchType,
    previewUrl: fileData.previewUrl || null,
  }
}

/**
 * @param {object} fileData
 * @param {{
 *   fetchHeaders?: Record<string,string>,
 *   studyInstanceUID?: string|null,
 *   assetBaseUrl?: string|null,
 *   skipIdbRead?: boolean,
 *   skipIdbWrite?: boolean,
 *   memoryOnly?: boolean,
 * }} [opts]
 * @returns {Promise<object>} item parcheado
 */
export async function upgradePreviewFrameToDicom(fileData, opts = {}) {
  if (!fileData) throw new Error('Frame vacío')

  if (!frameNeedsDicomUpgrade(fileData)) {
    return fileData
  }

  if (!frameHasDicomSource(fileData)) {
    throw new Error('Este frame no tiene DICOM disponible')
  }

  const sop =
    (fileData.sopInstanceUID != null && String(fileData.sopInstanceUID).trim()) ||
    null
  const studyUid =
    (opts.studyInstanceUID != null && String(opts.studyInstanceUID)) ||
    (fileData.study && fileData.study.studyId) ||
    ''

  const inst = buildInstFromFileData(fileData)
  const directUrl =
    inst.dicomUrl && /^https?:\/\//i.test(inst.dicomUrl) ? inst.dicomUrl : null

  // On-demand (ruler, ROIs): priorizar RAM; no bloquear por cuota IDB
  const memoryOnly = opts.memoryOnly !== false
  const skipIdbWrite = memoryOnly || !!opts.skipIdbWrite
  const skipIdbRead = !!opts.skipIdbRead

  const loaded = await loadInstanceForMode({
    renderMode: RENDER_MODE.DIAGNOSTIC,
    absoluteUrl: directUrl,
    assetBaseUrl: opts.assetBaseUrl != null ? opts.assetBaseUrl : null,
    studyInstanceUID: studyUid,
    fetchHeaders: opts.fetchHeaders,
    inst,
    skipIdbWrite,
    skipIdbRead,
  })

  if (!loaded || !loaded.image) {
    throw new Error('No se pudo decodificar el DICOM del frame')
  }

  return {
    ...fileData,
    image: loaded.image || null,
    imageId: loaded.imageId,
    sopInstanceUID: loaded.sopInstanceUID || sop || fileData.sopInstanceUID,
    dicomUrl: fileData.dicomUrl || loaded.absoluteUrl || null,
    archiveUrl: fileData.archiveUrl || inst.archiveUrl || null,
    byteOffset:
      fileData.byteOffset != null ? fileData.byteOffset : inst.byteOffset,
    byteLength:
      fileData.byteLength != null ? fileData.byteLength : inst.byteLength,
    dicomFetchType: fileData.dicomFetchType || inst.dicomFetchType || null,
    renderKind: 'dicom',
    usedFallback: false,
    fromCache: !!loaded.fromCache,
    skeleton: false,
    error: undefined,
  }
}
